import { defineStore } from 'pinia';
import { ref } from 'vue';
import { AxiosError } from 'axios';

import { ApiResponse } from '@/shared/types/api';

import householdsApi from '@/features/household/api/household';

import { Household } from '@/features/household/models/household';
import {
  CreateHouseholdForm,
  UpdateHouseholdNameForm,
  UpdateHouseholdSettingsForm,
} from '@/features/household/form/household_form';

export const useHouseholdStore = defineStore('householdStore', () => {
  const household = ref<Household | null>(null);

  const error = ref<string | null>(null);

  function initHousehold(data: Household) {
    household.value = data;
  }

  function clearHousehold() {
    household.value = null;
    error.value = null;
  }

  async function getHousehold(): Promise<ApiResponse<Household>> {
    try {
      const response = await householdsApi.getHousehold();
      
      if (response.status === 'success' && response.data) {
        initHousehold(response.data);
        return {
          status: 'success',
          data: response.data
        };
      }

      return {
        status: 'error',
        error: {
          code: 400,
          message: 'Failed to get household'
        }
      };
    } catch (error) {
      const _error = error as AxiosError<string>;
      return {
        status: 'error',
        error: {
          code: _error.response?.status || 500,
          message: 'Failed to get household'
        }
      };
    }
  }

  async function createHousehold(input: CreateHouseholdForm): Promise<ApiResponse<null>> {
    try {
      const response = await householdsApi.createHousehold(input);

      if (response.status === 'success' && response.data) {
        initHousehold(response.data);
        return {
          status: 'success',
          data: null
        };
      }

      return {
        status: 'error',
        error: {
          code: 400,
          message: 'Failed to create household'
        }
      };
    } catch (error) {
      const _error = error as AxiosError<string>;
      return {
        status: 'error',
        error: {
          code: _error.response?.status || 500,
          message: 'Failed to create household'
        }
      };
    }
  }

  async function updateHouseholdName(input: UpdateHouseholdNameForm): Promise<ApiResponse<null>> {
    try {
      const response = await householdsApi.updateHouseholdName(input);

      if (response.status === 'success' && response.data) {
        initHousehold(response.data);
        return {
          status: 'success',
          data: null
        };
      }

      return {
        status: 'error',
        error: {
          code: 400,
          message: 'Failed to update household name'
        }
      };
    } catch (error) {
      const _error = error as AxiosError<string>;
      return {
        status: 'error',
        error: {
          code: _error.response?.status || 500,
          message: 'Failed to update household name'
        }
      };
    }
  }

  async function updateHouseholdSettings(input: UpdateHouseholdSettingsForm): Promise<ApiResponse<null>> {
    try {
      const response = await householdsApi.updateHouseholdSettings(input);

      if (response.status === 'success' && response.data) {
        initHousehold(response.data);
        return {
          status: 'success',
          data: null
        };
      }

      return {
        status: 'error',
        error: {
          code: 400,
          message: 'Failed to update household settings'
        }
      };
    } catch (error) {
      const _error = error as AxiosError<string>;
      return {
        status: 'error',
        error: {
          code: _error.response?.status || 500,
          message: 'Failed to update household settings'
        }
      };
    }
  }

  return {
    household,
    error,
    initHousehold,
    clearHousehold,
    getHousehold,
    createHousehold,
    updateHouseholdName,
    updateHouseholdSettings
  }
})
